import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Collapse,
  Divider,
  useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon, ChevronRight as ChevronRightIcon, LiveTv as LiveTvIcon, ExpandMore as ExpandMoreIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import OddsList from '../OddsList';
import TeamBadge from '../TeamBadge';
import { OddsListSkeleton } from '../SkeletonLoaders';
import { TEAM_DIRECTORY } from '../../data/teamDirectory';
import { fetchScoresForSport, fetchEvents, fetchOdds, getSports, getAllSportsRaw } from '../../services/oddsApi';
import { fetchSportsNews } from '../../services/newsService';
import { fetchBroadcasts } from '../../services/broadcastService';
import { resolveWatchDestination, WATCH_DISCLOSURE } from '../../data/watchLinks';
import BackButton from '../BackButton';

// One entry per sport in the All Sports menu: where its data lives.
export const SPORT_HUB_INFO = {
  nfl: { display: 'NFL', apiKey: 'americanfootball_nfl', league: 'NFL', newsSport: 'NFL', playerProps: true, espn: 'NFL', family: 'americanfootball_nfl' },
  nba: { display: 'NBA', apiKey: 'basketball_nba', league: 'NBA', newsSport: 'NBA', playerProps: true, espn: 'NBA', family: 'basketball_nba' },
  mlb: { display: 'MLB', apiKey: 'baseball_mlb', league: 'MLB', newsSport: 'MLB', playerProps: true, espn: 'MLB', family: 'baseball_mlb' },
  nhl: { display: 'NHL', apiKey: 'icehockey_nhl', league: 'NHL', newsSport: 'NHL', playerProps: true, espn: 'NHL', family: 'icehockey_nhl' },
  soccer: { display: 'Soccer', apiKey: 'soccer_epl', league: 'Soccer', newsSport: 'MLS', playerProps: true, espn: 'SOCCER', family: 'soccer_epl' },
  ncaaf: { display: 'NCAAF', apiKey: 'americanfootball_ncaaf', league: 'NCAAF', newsSport: 'NCAAF', playerProps: false, espn: 'NCAAF', family: 'americanfootball_ncaaf' },
  ncaab: { display: 'NCAAB', apiKey: 'basketball_ncaab', league: 'NCAAB', newsSport: 'NCAAB', playerProps: false, espn: 'NCAAB', family: 'basketball_ncaab' },
  wnba: { display: 'WNBA', apiKey: 'basketball_wnba', league: 'WNBA', newsSport: 'WNBA', playerProps: true, espn: 'WNBA', family: 'basketball_wnba' },
  ufc: { display: 'UFC', apiKey: 'mma_mixed_martial_arts', league: 'UFC', newsSport: 'MMA', playerProps: false, espn: 'UFC', family: 'mma_' },
  boxing: { display: 'Boxing', apiKey: 'boxing_boxing', league: 'Boxing', newsSport: 'Boxing', playerProps: false, espn: 'BOXING', family: 'boxing_' },
  tennis: { display: 'Tennis', apiKey: 'tennis_atp', league: 'Tennis', newsSport: 'Tennis', playerProps: false, espn: 'TENNIS', family: 'tennis_' },
  golf: { display: 'Golf', apiKey: 'golf_pga_championship', league: 'Golf', newsSport: 'Golf', playerProps: false, espn: 'GOLF', family: 'golf_' },
};

export const HUB_SECTIONS = ['overview', 'scores', 'schedule', 'odds', 'futures', 'props', 'news', 'teams'];

// Sports the Parlay Builder covers at launch (Phase 1: game lines).
const PARLAY_BUILDER_SPORT_KEYS = ['americanfootball_nfl', 'americanfootball_ncaaf', 'basketball_nba', 'icehockey_nhl'];

// Hubs with a Player Form panel on schedule cards: hub slug -> the
// player-stats league. Only the four leagues the free stats layer
// covers (playerStats / matchup-form function); other hubs render
// no toggle.
const FORM_LEAGUE_BY_SLUG = { nba: 'nba', nfl: 'nfl', nhl: 'nhl', mlb: 'mlb' };

// Display labels for the matchup-form response stat keys.
const FORM_STAT_LABELS = {
  pts: 'PTS', reb: 'REB', ast: 'AST', fg3m: '3PM',
  passYds: 'PASS YDS', passTd: 'PASS TD', rushYds: 'RUSH YDS', recYds: 'REC YDS',
  receptions: 'REC', rushTd: 'RUSH TD', recTd: 'REC TD', targets: 'TGT',
  goals: 'G', assists: 'A', points: 'PTS', shots: 'SOG',
  hits: 'H', totalBases: 'TB', rbi: 'RBI', homeRuns: 'HR', strikeOuts: 'K', earnedRuns: 'ER',
};

const sectionLabel = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const formatGameTime = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const formatDateHead = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
};

// --- Where to Watch helpers ---
const normTeam = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const etDayKey = (iso) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(iso));
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch (e) {
    return new Date(iso).toDateString();
  }
};

const etYmd = (iso) => etDayKey(iso).replace(/-/g, '');

// --- Player Form helpers ---
// Schedule games carry full team names (Odds API). Resolve the
// site's abbreviation from the team directory; when the directory
// has no entry, pass the full name through — the matchup-form
// function resolves names server-side too.
const siteAbbrFor = (teamName, leagueDisplay) => {
  const entry = TEAM_DIRECTORY.find(
    (t) => t.league === leagueDisplay && normTeam(t.name) === normTeam(teamName)
  );
  return entry ? entry.abbr : teamName;
};

const EmptyCard = ({ title, body, action }) => (
  <Card sx={{ mt: 1 }}>
    <CardContent sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: action ? 3 : 0 }}>
        {body}
      </Typography>
      {action}
    </CardContent>
  </Card>
);

const SportHubScreen = ({ odds, loading, error, onLoadOdds }) => {
  const { sportKey, section } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  // Where to Watch is highlighted in the brand orange in dark mode only;
  // light mode renders exactly as before.
  const isDark = theme.palette.mode === 'dark';

  const slug = sportKey?.toLowerCase();
  const info = SPORT_HUB_INFO[slug];
  const activeSection = HUB_SECTIONS.includes(section) ? section : 'overview';

  const [headlines, setHeadlines] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [liveGames, setLiveGames] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [propEvents, setPropEvents] = useState([]);
  const [outrights, setOutrights] = useState([]);
  const [outrightsLoading, setOutrightsLoading] = useState(false);
  const [propsLoading, setPropsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [watchStatus, setWatchStatus] = useState('idle');
  const [watchEvents, setWatchEvents] = useState([]);
  const [watchOpenId, setWatchOpenId] = useState(null);
  const [formOpenId, setFormOpenId] = useState(null);
  const [formByGameId, setFormByGameId] = useState({});
  const formLeague = FORM_LEAGUE_BY_SLUG[slug];
  const [futuresStatus, setFuturesStatus] = useState('idle');
  const [futuresMarkets, setFuturesMarkets] = useState([]);
  const [futuresExpanded, setFuturesExpanded] = useState(null);

  // Invalid sport -> 404, unknown section -> overview
  useEffect(() => {
    if (!info) {
      navigate('/404', { replace: true });
    } else if (section && !HUB_SECTIONS.includes(section)) {
      navigate(`/sport/${slug}/overview`, { replace: true });
    }
  }, [info, section, slug, navigate]);

  const sportGames = useMemo(
    () =>
      [...(odds || [])]
        .filter((g) => g.sport_key === info?.apiKey)
        .sort((a, b) => new Date(a.commence_time) - new Date(b.commence_time)),
    [odds, info]
  );

  // Load this sport's odds when we don't have them yet (same pattern as SportScreen)
  useEffect(() => {
    if (!info || !onLoadOdds) return;
    if (sportGames.length === 0 && !loading) {
      onLoadOdds(info.apiKey).catch(() => {});
    }
  }, [info?.apiKey]);

  // Section data loads
  useEffect(() => {
    if (!info) return;
    let cancelled = false;

    if (activeSection === 'news' || activeSection === 'overview') {
      setNewsLoading(true);
      fetchSportsNews(info.newsSport, activeSection === 'news' ? 8 : 3)
        .then((data) => {
          if (!cancelled) setHeadlines(Array.isArray(data) ? data : []);
        })
        .finally(() => {
          if (!cancelled) setNewsLoading(false);
        });
    }

    if (activeSection === 'scores') {
      setLiveLoading(true);
      fetchScoresForSport(info.apiKey)
        .then((games) => {
          if (!cancelled) setLiveGames(games || []);
        })
        .catch(() => {
          if (!cancelled) setLiveGames([]);
        })
        .finally(() => {
          if (!cancelled) setLiveLoading(false);
        });
    }

    if (activeSection === 'futures') {
      setFuturesStatus('loading');
      (async () => {
        try {
          const allSports = await getAllSportsRaw();
          const keys = (allSports || [])
            .filter(
              (s) =>
                s.key &&
                s.key.startsWith(info.family) &&
                s.key !== info.apiKey &&
                (s.key.includes('_winner') || s.key.includes('_champion'))
            )
            .slice(0, 6);
          const PREF = ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'espnbet', 'fanatics', 'bet365', 'hardrockbet', 'williamhill_us'];
          const markets = [];
          for (const s of keys) {
            try {
              const events = await fetchOdds(s.key, 'us', 'outrights');
              const ev = events?.[0];
              if (!ev) continue;
              const booksPresent = [];
              const byName = new Map();
              (ev.bookmakers || []).forEach((b) => {
                const mkt = b.markets?.find((x) => x.key === 'outrights');
                if (!mkt) return;
                if (!booksPresent.find((x) => x.key === b.key)) booksPresent.push({ key: b.key, title: b.title });
                (mkt.outcomes || []).forEach((o) => {
                  if (!byName.has(o.name)) byName.set(o.name, {});
                  byName.get(o.name)[b.key] = o.price;
                });
              });
              if (!byName.size || !booksPresent.length) continue;
              const rank = (k) => (PREF.indexOf(k) === -1 ? 99 : PREF.indexOf(k));
              const cols = [...booksPresent].sort((a, b) => rank(a.key) - rank(b.key)).slice(0, 4);
              const rows = [...byName.entries()]
                .map(([name, prices]) => {
                  const cells = cols.map((c) => (prices[c.key] !== undefined ? prices[c.key] : null));
                  const valid = cells.filter((x) => x !== null);
                  const bestPrice = valid.length ? Math.max(...valid) : null;
                  return { name, cells, bestPrice, bestIdx: bestPrice === null ? -1 : cells.indexOf(bestPrice) };
                })
                .filter((r) => r.bestPrice !== null)
                .sort((a, b) => a.bestPrice - b.bestPrice);
              if (rows.length) markets.push({ key: s.key, title: ev.sport_title || s.title || s.key, books: cols, rows });
            } catch (e) {
              // skip this futures market
            }
          }
          if (!cancelled) {
            setFuturesMarkets(markets);
            setFuturesStatus('ready');
          }
        } catch (e) {
          if (!cancelled) setFuturesStatus('error');
        }
      })();
    }

    if (activeSection === 'props' && (slug === 'golf' || slug === 'tennis')) {
      setOutrightsLoading(true);
      (async () => {
        try {
          const allSports = await getAllSportsRaw();
          const prefix = slug === 'golf' ? 'golf_' : 'tennis_';
          const winnerKeys = (allSports || [])
            .filter((s) => s.key?.startsWith(prefix) && s.key.includes('_winner'))
            .slice(0, 2);
          const loaded = [];
          for (const s of winnerKeys) {
            try {
              const events = await fetchOdds(s.key, 'us', 'outrights');
              const event = events?.[0];
              if (!event) continue;
              const best = new Map();
              (event.bookmakers || []).forEach((b) => {
                const market = b.markets?.find((m) => m.key === 'outrights');
                (market?.outcomes || []).forEach((o) => {
                  if (!best.has(o.name) || o.price > best.get(o.name)) best.set(o.name, o.price);
                });
              });
              const outcomes = [...best.entries()]
                .map(([name, price]) => ({ name, price }))
                .sort((a, b) => a.price - b.price)
                .slice(0, 15);
              if (outcomes.length) loaded.push({ title: s.title || event.sport_title || s.key, outcomes });
            } catch (e) {
              // skip this outright if it fails
            }
          }
          if (!cancelled) setOutrights(loaded);
        } catch (e) {
          if (!cancelled) setOutrights([]);
        } finally {
          if (!cancelled) setOutrightsLoading(false);
        }
      })();
    }

    if (activeSection === 'props') {
      setPropsLoading(true);
      fetchEvents(info.apiKey)
        .then((data) => {
          if (!cancelled) setPropEvents(data || []);
        })
        .catch(() => {
          if (!cancelled) setPropEvents([]);
        })
        .finally(() => {
          if (!cancelled) setPropsLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [info, activeSection]);

  if (!info) return null;

  const handleRefresh = async () => {
    if (!onLoadOdds || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onLoadOdds(info.apiKey);
    } finally {
      setIsRefreshing(false);
    }
  };

  const leagueTeams = TEAM_DIRECTORY.filter((t) => t.league === info.league);

  const renderOverview = () => (
    <Box>
      {sportGames.length > 0 ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            Next up
          </Typography>
          <OddsList odds={sportGames.slice(0, 2)} selectedSport={{ key: info.apiKey, title: info.display }} isHomeScreen={false} />
          <Button sx={{ mt: 1, fontWeight: 700 }} onClick={() => navigate(`/sport/${slug}/odds`)}>
            Full {info.display} odds board
          </Button>
        </>
      ) : (
        <EmptyCard
          title={`No ${info.display} games on the board right now`}
          body="Upcoming games will appear here as soon as the books post them."
          action={
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isRefreshing || loading}>
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          }
        />
      )}

      <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 1.5 }}>
        Latest {info.display} headlines
      </Typography>
      {newsLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Stack spacing={1.25}>
          {headlines.slice(0, 3).map((h, i) => (
            <Card key={h.guid || i}>
              <CardActionArea component="a" href={h.link} target="_blank" rel="noopener noreferrer">
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {h.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {h.source} · {new Date(h.pubDate).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
      <Button sx={{ mt: 1, fontWeight: 700 }} onClick={() => navigate(`/sport/${slug}/news`)}>
        All {info.display} news
      </Button>
    </Box>
  );

  const ensureWatchLoaded = () => {
    if (!info?.espn || watchStatus === 'loading' || watchStatus === 'ready') return;
    if (sportGames.length === 0) return;
    setWatchStatus('loading');
    const times = sportGames.map((g) => g.commence_time).sort();
    const dates = `${etYmd(times[0])}-${etYmd(times[times.length - 1])}`;
    fetchBroadcasts(info.espn, dates)
      .then((events) => {
        setWatchEvents(Array.isArray(events) ? events : []);
        setWatchStatus('ready');
      })
      .catch(() => setWatchStatus('error'));
  };

  const toggleWatch = (game) => {
    setWatchOpenId((prev) => (prev === game.id ? null : game.id));
    ensureWatchLoaded();
  };

  const findWatch = (game) => {
    if (watchStatus !== 'ready') return null;
    const day = etDayKey(game.commence_time);
    const home = normTeam(game.home_team);
    const away = normTeam(game.away_team);
    return (
      watchEvents.find((ev) => {
        if (etDayKey(ev.start) !== day) return false;
        const eh = normTeam(ev.home);
        const ea = normTeam(ev.away);
        if (eh && ea) return (eh === home && ea === away) || (eh === away && ea === home);
        const label = normTeam(ev.name);
        return label.includes(home) || label.includes(away);
      }) || null
    );
  };

  const renderWatchPanel = (game) => {
    if (watchStatus === 'loading' || watchStatus === 'idle') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading broadcast info…
          </Typography>
        </Box>
      );
    }
    if (watchStatus === 'error') {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Broadcast info couldn't be loaded right now.
        </Typography>
      );
    }
    const ev = findWatch(game);
    if (!ev) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No broadcast listing found for this game yet — listings are usually posted a few days before the game.
        </Typography>
      );
    }
    if (ev.tv.length === 0 && ev.streaming.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Broadcast info hasn't been posted for this game yet.
        </Typography>
      );
    }
    const group = (label, entries) =>
      entries.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary' }}>
            {label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
            {entries.map((e) => {
              const chipLabel = e.market && e.market !== 'National' ? `${e.name} · ${e.market}` : e.name;
              const url = resolveWatchDestination(e.name);
              // Known destination: a real Watch button that links OUT to
              // the official stream (never embedded). Unknown outlet:
              // plain listing text, exactly as before.
              return url ? (
                <Chip
                  key={`${label}-${e.name}`}
                  label={`${chipLabel} \u2197`}
                  size="small"
                  variant="outlined"
                  color={isDark ? 'warning' : 'primary'}
                  clickable
                  component="a"
                  href={url}
                  target="_blank"
                  rel="sponsored noopener noreferrer"
                />
              ) : (
                <Chip key={`${label}-${e.name}`} label={chipLabel} size="small" variant="outlined" />
              );
            })}
          </Box>
        </Box>
      );
    return (
      <Box sx={{ py: 1 }}>
        {group('TV', ev.tv)}
        {group('STREAMING', ev.streaming)}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Listings via ESPN; streaming includes each network's own apps and services.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {WATCH_DISCLOSURE}
        </Typography>
      </Box>
    );
  };

  // Player Form: lazy-fetch the matchup-form function on first
  // expand and cache the result per game; unexpanded cards never
  // fetch. Errors stay retryable on the next expand.
  const toggleForm = (game) => {
    const opening = formOpenId !== game.id;
    setFormOpenId(opening ? game.id : null);
    if (!opening || !formLeague) return;
    const existing = formByGameId[game.id];
    if (existing && existing.status !== 'error') return;
    setFormByGameId((prev) => ({ ...prev, [game.id]: { status: 'loading' } }));
    const qs = new URLSearchParams({
      league: formLeague,
      home: siteAbbrFor(game.home_team, info.league),
      away: siteAbbrFor(game.away_team, info.league),
    });
    fetch(`/.netlify/functions/matchup-form?${qs.toString()}`)
      .then((res) => {
        if (!res.ok) throw new Error(`matchup-form ${res.status}`);
        return res.json();
      })
      .then((data) => setFormByGameId((prev) => ({ ...prev, [game.id]: { status: 'ready', data } })))
      .catch(() => setFormByGameId((prev) => ({ ...prev, [game.id]: { status: 'error' } })));
  };

  const renderFormPanel = (game) => {
    const entry = formByGameId[game.id];
    if (!entry || entry.status === 'loading') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading player form…
          </Typography>
        </Box>
      );
    }
    if (entry.status === 'error') {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Player form unavailable right now.
        </Typography>
      );
    }
    const renderTeam = (team) => (
      <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
          {team.abbrev}
        </Typography>
        {!team.matched || team.players.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Player form for this matchup updates nightly.
          </Typography>
        ) : (
          team.players.map((player) => {
            const primaryLabel = FORM_STAT_LABELS[player.primary] || player.primary;
            const l5 = player.averages?.L5?.[player.primary];
            const l10 = player.averages?.L10?.[player.primary];
            const others = Object.keys(player.averages?.L10 || {}).filter((k) => k !== player.primary);
            // The API returns lastFive newest first; the strip reads oldest -> newest.
            const strip = [...(player.lastFive || [])].reverse();
            return (
              <Box key={player.playerName} sx={{ mb: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {player.playerName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {primaryLabel} L5 {l5 ?? '–'} · L10 {l10 ?? '–'}
                  {others.length > 0 &&
                    ` — ${others.map((k) => `${FORM_STAT_LABELS[k] || k} ${player.averages.L10[k]}`).join(' · ')}`}
                </Typography>
                {strip.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {strip.map((g, i) => (
                      <Box
                        key={i}
                        sx={{
                          minWidth: 26,
                          textAlign: 'center',
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {g.stats?.[player.primary] ?? '–'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>
    );
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, py: 1 }}>
        {renderTeam(entry.data.away)}
        {renderTeam(entry.data.home)}
      </Box>
    );
  };

  const renderScores = () => {
    const getScore = (game, teamName) => game.scores?.find((s) => s.name === teamName)?.score;
    const sorted = [...liveGames].sort((a, b) => {
      const rank = (g) => (g.completed ? 2 : g.scores?.length || new Date(g.commence_time) < new Date() ? 0 : 1);
      const diff = rank(a) - rank(b);
      return diff !== 0 ? diff : new Date(b.commence_time) - new Date(a.commence_time);
    });
    return (
      <Box>
        {liveLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : sorted.length === 0 ? (
          <EmptyCard
            title={`No recent ${info.display} scores`}
            body="Live and final scores from the last few days appear here. Upcoming games are on the Schedule tab."
            action={
              <Button variant="contained" onClick={() => navigate(`/sport/${slug}/schedule`)}>
                View schedule
              </Button>
            }
          />
        ) : (
          <Stack spacing={1.25}>
            {sorted.map((game) => {
              const awayScore = getScore(game, game.away_team);
              const homeScore = getScore(game, game.home_team);
              const started = game.scores?.length > 0 || new Date(game.commence_time) < new Date();
              return (
                <Card key={game.id}>
                  <CardContent sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
                        <TeamBadge teamName={game.away_team} size={26} />
                        <Typography variant="body1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                          {game.away_team}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 800 }}>
                          {awayScore ?? '–'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <TeamBadge teamName={game.home_team} size={26} />
                        <Typography variant="body1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                          {game.home_team}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 800 }}>
                          {homeScore ?? '–'}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={game.completed ? 'Final' : started ? 'Live' : formatGameTime(game.commence_time)}
                      size="small"
                      color={game.completed ? 'default' : started ? 'warning' : 'default'}
                      variant={started && !game.completed ? 'contained' : 'outlined'}
                      sx={{ fontWeight: 700 }}
                    />
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Box>
    );
  };

  const renderSchedule = () => {
    if (loading && sportGames.length === 0) return <OddsListSkeleton count={4} />;
    if (sportGames.length === 0) {
      return (
        <EmptyCard
          title={`No scheduled ${info.display} games available`}
          body="There are currently no upcoming games with posted odds for this sport."
          action={
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isRefreshing || loading}>
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          }
        />
      );
    }
    const groups = [];
    let lastDate = '';
    sportGames.forEach((g) => {
      const dateKey = new Date(g.commence_time).toDateString();
      if (dateKey !== lastDate) {
        groups.push({ type: 'date', label: formatDateHead(g.commence_time), key: dateKey });
        lastDate = dateKey;
      }
      groups.push({ type: 'game', game: g, key: g.id });
    });
    return (
      <Box>
        {groups.map((item) =>
          item.type === 'date' ? (
            <Typography key={item.key} variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 1 }}>
              {item.label}
            </Typography>
          ) : (
            <Card key={item.key} sx={{ mb: 1.25 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                  onClick={() => navigate(`/sport/${slug}/odds`)}
                >
                  <TeamBadge teamName={item.game.away_team} size={30} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {item.game.away_team} @ {item.game.home_team}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatGameTime(item.game.commence_time)}
                    </Typography>
                  </Box>
                  <TeamBadge teamName={item.game.home_team} size={30} />
                </Box>
                <Divider sx={{ my: 1.25 }} />
                <Button
                  size="small"
                  startIcon={<LiveTvIcon />}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transform: watchOpenId === item.game.id ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                      }}
                    />
                  }
                  onClick={() => toggleWatch(item.game)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: isDark ? 700 : 600,
                    ...(isDark && { color: '#FFFFFF' }),
                  }}
                >
                  Where to Watch
                </Button>
                <Collapse in={watchOpenId === item.game.id} timeout="auto" unmountOnExit>
                  {renderWatchPanel(item.game)}
                </Collapse>
                {formLeague && (
                  <>
                    <Button
                      size="small"
                      startIcon={<TrendingUpIcon />}
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: formOpenId === item.game.id ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s',
                          }}
                        />
                      }
                      onClick={() => toggleForm(item.game)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Player Form
                    </Button>
                    <Collapse in={formOpenId === item.game.id} timeout="auto" unmountOnExit>
                      {renderFormPanel(item.game)}
                    </Collapse>
                  </>
                )}
              </CardContent>
            </Card>
          )
        )}
      </Box>
    );
  };

  const renderOdds = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Chip label={`${sportGames.length} games`} size="small" variant="outlined" />
        <Button size="small" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isRefreshing || loading}>
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && sportGames.length === 0 ? (
        <OddsListSkeleton count={4} />
      ) : sportGames.length > 0 ? (
        <OddsList odds={sportGames} selectedSport={{ key: info.apiKey, title: info.display }} isHomeScreen={false} />
      ) : (
        <EmptyCard
          title={`No games available for ${info.display}`}
          body="There are currently no scheduled games or betting odds available for this sport."
        />
      )}
    </Box>
  );

  const renderFutures = () => {
    if (futuresStatus === 'loading' || futuresStatus === 'idle') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
          <CircularProgress size={22} />
          <Typography color="text.secondary">Loading {info.display} futures…</Typography>
        </Box>
      );
    }
    if (futuresStatus === 'error') {
      return (
        <EmptyCard
          title="Futures couldn't be loaded"
          body="The futures markets didn't load. Please try again in a moment."
        />
      );
    }
    if (futuresMarkets.length === 0) {
      return (
        <EmptyCard
          title={`No futures markets posted for ${info.display}`}
          body="Championship, conference and division winner odds appear here when sportsbooks post them for this sport."
        />
      );
    }
    return (
      <Stack spacing={2.5}>
        {futuresMarkets.map((m) => {
          const expanded = futuresExpanded === m.key;
          const rows = expanded ? m.rows : m.rows.slice(0, 12);
          const gridCols = `minmax(150px, 1.2fr) repeat(${m.books.length}, minmax(70px, 1fr))`;
          return (
            <Card key={m.key}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 1.75,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {m.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Best price highlighted
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, gap: 0.75, px: 1.75, pt: 1 }}>
                <span />
                {m.books.map((b) => (
                  <Typography
                    key={b.key}
                    variant="caption"
                    sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary' }}
                  >
                    {b.title}
                  </Typography>
                ))}
              </Box>
              {rows.map((r) => (
                <Box
                  key={r.name}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: gridCols,
                    gap: 0.75,
                    alignItems: 'center',
                    px: 1.75,
                    py: 0.9,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <TeamBadge teamName={r.name} size={26} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {r.name}
                    </Typography>
                  </Box>
                  {r.cells.map((p, i) => (
                    <Box
                      key={i}
                      sx={{
                        textAlign: 'center',
                        py: 0.8,
                        px: 0.5,
                        fontWeight: 800,
                        fontSize: 15,
                        borderRadius: 0.5,
                        bgcolor: i === r.bestIdx ? '#e8721f' : 'action.hover',
                        color: i === r.bestIdx ? '#fff' : 'text.primary',
                      }}
                    >
                      {p === null ? '—' : p > 0 ? `+${p}` : p}
                    </Box>
                  ))}
                </Box>
              ))}
              {m.rows.length > 12 && (
                <Button size="small" onClick={() => setFuturesExpanded(expanded ? null : m.key)} sx={{ m: 1 }}>
                  {expanded ? 'Show fewer' : `Show all ${m.rows.length}`}
                </Button>
              )}
            </Card>
          );
        })}
        <Typography variant="caption" color="text.secondary">
          Futures odds via The Odds API. Each column is a sportsbook's current outright price.
        </Typography>
      </Stack>
    );
  };

  const renderProps = () => {
    if (propsLoading) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }
    if (propEvents.length === 0) {
      return (
        <EmptyCard
          title={`No upcoming ${info.display} games`}
          body="Games with betting markets will appear here as the books post them."
        />
      );
    }
    const outrightsBlock = (slug === 'golf' || slug === 'tennis') && (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Outrights — tournament winner
        </Typography>
        {outrightsLoading ? (
          <CircularProgress size={24} />
        ) : outrights.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No outright markets are posted right now — they appear when a tournament is on the schedule.
          </Typography>
        ) : (
          outrights.map((o) => (
            <Card key={o.title} sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: 1.75 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  {o.title}
                </Typography>
                {o.outcomes.map((oc) => (
                  <Box key={oc.name} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
                    <Typography variant="body2">{oc.name}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {oc.price > 0 ? `+${oc.price}` : oc.price}
                    </Typography>
                  </Box>
                ))}
                <Typography variant="caption" color="text.secondary">
                  Best available price across books
                </Typography>
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    );

    return (
      <Box>
        {outrightsBlock}
        <Stack spacing={1.5}>
          {propEvents.map((event) => (
            <Card key={event.id} sx={{ '&:hover': { boxShadow: 4 }, transition: 'box-shadow 0.2s' }}>
              <CardActionArea
                onClick={() =>
                  navigate(`/player-props/${info.apiKey}/${event.id}`, { state: { event } })
                }
              >
                <CardContent sx={{ py: 1.5, px: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TeamBadge teamName={event.away_team} size={30} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {event.away_team} @ {event.home_team}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatGameTime(event.commence_time)} · Tap for player props
                    </Typography>
                  </Box>
                  <TeamBadge teamName={event.home_team} size={30} />
                  <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
        {!info.playerProps && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The odds feed doesn't carry player props for {info.display} yet — this list shows every
            upcoming {info.display} game, and game markets for each are on the Odds tab.
          </Typography>
        )}
      </Box>
    );
  };

  const renderNews = () => (
    <Box>
      {newsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : headlines.length === 0 ? (
        <EmptyCard title={`No ${info.display} headlines right now`} body="The news feed is quiet for this sport at the moment." />
      ) : (
        <Stack spacing={1.25}>
          {headlines.map((h, i) => (
            <Card key={h.guid || i}>
              <CardActionArea component="a" href={h.link} target="_blank" rel="noopener noreferrer">
                <CardContent sx={{ py: 1.75 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {h.title}
                  </Typography>
                  {h.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                      {h.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {h.source} · {new Date(h.pubDate).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );

  const renderTeams = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {leagueTeams.length} teams in the directory. Tap a team to see its odds.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 1.5,
        }}
      >
        {leagueTeams.map((team) => (
          <Card key={team.abbr}>
            <CardActionArea onClick={() => navigate(`/sport/${slug}/odds`)}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.75 }}>
                <TeamBadge teamName={team.name} size={38} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {team.abbr}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {team.name}
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box>
      <BackButton />
      {/* Hub header + section tabs */}
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            <Box component="span" sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => navigate('/allsports')}>
              All Sports
            </Box>{' '}
            / {info.display}
          </Typography>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mt: 0.5 }}>
            {info.display} {sectionLabel(activeSection)}
          </Typography>
        </Box>
        {PARLAY_BUILDER_SPORT_KEYS.includes(info.apiKey) && (
          <Button
            variant="outlined"
            size="small"
            sx={{ fontWeight: 700, textTransform: 'none', mt: 0.5 }}
            onClick={() => navigate(`/parlay-builder?sport=${info.apiKey}`)}
          >
            Parlay Builder
          </Button>
        )}
      </Box>

      <Tabs
        value={activeSection}
        onChange={(_, value) => navigate(`/sport/${slug}/${value}`)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        {HUB_SECTIONS.map((s) => (
          <Tab key={s} value={s} label={sectionLabel(s)} sx={{ textTransform: 'none', fontWeight: 600 }} />
        ))}
      </Tabs>

      {activeSection === 'overview' && renderOverview()}
      {activeSection === 'scores' && renderScores()}
      {activeSection === 'schedule' && renderSchedule()}
      {activeSection === 'futures' && renderFutures()}
      {activeSection === 'odds' && renderOdds()}
      {activeSection === 'props' && renderProps()}
      {activeSection === 'news' && renderNews()}
      {activeSection === 'teams' && renderTeams()}
    </Box>
  );
};

export default SportHubScreen;
